import React from 'react';
import { Dialog, Box, Typography, Button, Avatar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TEAM_COLORS } from '../theme';

const RondaHandSummaryModal = ({ open, summaryData, players, onContinue }) => {
    const { t } = useTranslation();

    if (!summaryData || !players) return null;

    const { team1Cards, team2Cards, team1Points, team2Points } = summaryData;

    // Determinar qué equipo recogió más cartas
    const team1CollectedMore = team1Cards > team2Cards;
    const team2CollectedMore = team2Cards > team1Cards;
    const isTie = team1Cards === team2Cards;

    // Helper function to check if avatar is emoji
    const isEmojiAvatar = (avatar) => {
        return typeof avatar === 'string' &&
            !avatar.startsWith('http') &&
            !avatar.startsWith('/');
    };

    return (
        <Dialog
            open={open}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1a472a 0%, #2d5a3d 50%, #1a472a 100%)',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
                    overflow: 'visible',
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: 'blur(8px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 2,
                    px: 2,
                }}
            >
                {/* Title */}
                <Typography
                    variant="h6"
                    sx={{
                        color: '#fff',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        mb: 2,
                        textAlign: 'center',
                    }}
                >
                    {t('ronda_hand_summary.title')}
                </Typography>

                {/* Card count summary */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        width: '100%',
                        mb: 2,
                    }}
                >
                    {/* Team 1 */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: team1CollectedMore 
                                ? 'rgba(255, 215, 0, 0.2)' 
                                : 'rgba(255, 255, 255, 0.1)',
                            borderRadius: 1.5,
                            p: 1.5,
                            border: team1CollectedMore 
                                ? '2px solid rgba(255, 215, 0, 0.5)' 
                                : 'none',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* Team color indicator */}
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    backgroundColor: TEAM_COLORS.team1.main,
                                    border: '2px solid rgba(255,255,255,0.3)',
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#fff',
                                    fontSize: '1rem',
                                    fontWeight: team1CollectedMore ? 'bold' : 'normal',
                                }}
                            >
                                {t('ronda_hand_summary.team1')}
                            </Typography>
                            {/* Player avatars */}
                            <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5 }}>
                                {/* Player 0 */}
                                {isEmojiAvatar(players[0]?.avatar) ? (
                                    <Box
                                        sx={{
                                            fontSize: '1.2rem',
                                            lineHeight: 1,
                                            width: 28,
                                            height: 28,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {players[0].avatar}
                                    </Box>
                                ) : (
                                    <Avatar
                                        src={players[0]?.avatar}
                                        sx={{ width: 28, height: 28 }}
                                    />
                                )}
                                {/* Player 2 */}
                                {isEmojiAvatar(players[2]?.avatar) ? (
                                    <Box
                                        sx={{
                                            fontSize: '1.2rem',
                                            lineHeight: 1,
                                            width: 28,
                                            height: 28,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {players[2].avatar}
                                    </Box>
                                ) : (
                                    <Avatar
                                        src={players[2]?.avatar}
                                        sx={{ width: 28, height: 28 }}
                                    />
                                )}
                            </Box>
                        </Box>
                        <Typography
                            sx={{
                                color: '#fff',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                            }}
                        >
                            {t('ronda_hand_summary.cards_count', { count: team1Cards })}
                            {team1Points > 0 && (
                                <Typography
                                    component="span"
                                    sx={{
                                        color: '#ffd700',
                                        fontSize: '0.9rem',
                                        ml: 1,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    (+{team1Points} {t('ronda_hand_summary.points')})
                                </Typography>
                            )}
                        </Typography>
                    </Box>

                    {/* Team 2 */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: team2CollectedMore 
                                ? 'rgba(255, 215, 0, 0.2)' 
                                : 'rgba(255, 255, 255, 0.1)',
                            borderRadius: 1.5,
                            p: 1.5,
                            border: team2CollectedMore 
                                ? '2px solid rgba(255, 215, 0, 0.5)' 
                                : 'none',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* Team color indicator */}
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    backgroundColor: TEAM_COLORS.team2.main,
                                    border: '2px solid rgba(255,255,255,0.3)',
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#fff',
                                    fontSize: '1rem',
                                    fontWeight: team2CollectedMore ? 'bold' : 'normal',
                                }}
                            >
                                {t('ronda_hand_summary.team2')}
                            </Typography>
                            {/* Player avatars */}
                            <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5 }}>
                                {/* Player 1 */}
                                {isEmojiAvatar(players[1]?.avatar) ? (
                                    <Box
                                        sx={{
                                            fontSize: '1.2rem',
                                            lineHeight: 1,
                                            width: 28,
                                            height: 28,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {players[1].avatar}
                                    </Box>
                                ) : (
                                    <Avatar
                                        src={players[1]?.avatar}
                                        sx={{ width: 28, height: 28 }}
                                    />
                                )}
                                {/* Player 3 */}
                                {isEmojiAvatar(players[3]?.avatar) ? (
                                    <Box
                                        sx={{
                                            fontSize: '1.2rem',
                                            lineHeight: 1,
                                            width: 28,
                                            height: 28,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {players[3].avatar}
                                    </Box>
                                ) : (
                                    <Avatar
                                        src={players[3]?.avatar}
                                        sx={{ width: 28, height: 28 }}
                                    />
                                )}
                            </Box>
                        </Box>
                        <Typography
                            sx={{
                                color: '#fff',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                            }}
                        >
                            {t('ronda_hand_summary.cards_count', { count: team2Cards })}
                            {team2Points > 0 && (
                                <Typography
                                    component="span"
                                    sx={{
                                        color: '#ffd700',
                                        fontSize: '0.9rem',
                                        ml: 1,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    (+{team2Points} {t('ronda_hand_summary.points')})
                                </Typography>
                            )}
                        </Typography>
                    </Box>
                </Box>

                {/* Winner message */}
                {!isTie && (
                    <Typography
                        sx={{
                            color: '#ffd700',
                            fontSize: '0.95rem',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            mb: 2,
                            textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
                        }}
                    >
                        {team1CollectedMore 
                            ? t('ronda_hand_summary.team1_wins') 
                            : t('ronda_hand_summary.team2_wins')}
                    </Typography>
                )}

                {isTie && (
                    <Typography
                        sx={{
                            color: '#fff',
                            fontSize: '0.95rem',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            mb: 2,
                            textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
                        }}
                    >
                        {t('ronda_hand_summary.tie')}
                    </Typography>
                )}

                {/* Continue button */}
                <Button
                    variant="contained"
                    onClick={onContinue}
                    sx={{
                        backgroundColor: '#ffd700',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: '#ffed4a',
                        },
                    }}
                >
                    {t('ronda_hand_summary.continue_button')}
                </Button>
            </Box>
        </Dialog>
    );
};

export default RondaHandSummaryModal;
